import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react'
import { AppName } from '@hb/shared'
import { SITE_ERRORS } from '@hb/shared/src/collections/names'
import { addDoc, collection } from 'firebase/firestore'
import React, { PropsWithChildren, useCallback, useState } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { auth, db } from '../../backend'
import { Loading } from '../Loading'

const ErrorReportingStatus = ({
  isReportingError,
  errorReported,
  errorWhileReporting,
}: {
  isReportingError: boolean
  errorReported: Date | null
  errorWhileReporting: string | null
}) => {
  if (isReportingError) return <Loading text="Reporting error..." />
  if (errorReported)
    return (
      <Text fontWeight={500} color="green.500">
        Crash successfully reported at {errorReported.toLocaleString()}
      </Text>
    )
  if (errorWhileReporting)
    return (
      <Text>Ironically, an error occurred while reporting the error: {errorWhileReporting}</Text>
    )
  return null
}

const ErrorFallback: React.FC<PropsWithChildren<FallbackProps>> = ({ error, children }) => (
  <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50" p={4}>
    <VStack spacing={4} maxW="600px" bg="white" p={8} borderRadius="lg" boxShadow="md">
      <Heading color="red.500">Something went wrong</Heading>
      <Text color="gray.600">
        Something unexpected happened. We are creating an error report, but please reach out to
        Hamilton Billing admins to make sure we fix the issue for you!
      </Text>
      {process.env.NODE_ENV === 'development' && (
        <Box p={4} bg="gray.100" borderRadius="md" w="100%" overflow="auto" maxH="200px">
          <Text fontSize="sm" color="gray.700">
            Error: {error.message}
          </Text>
          <Text fontSize="sm" color="gray.700" mt={2}>
            Stack: {error.stack}
          </Text>
        </Box>
      )}
      {children}
      {/* <Button colorScheme="blue" onClick={resetErrorBoundary} mt={4}>
        Ignore Error (May not work)
      </Button> */}
      <Button colorScheme="red" onClick={() => window.location.reload()}>
        Reload Site
      </Button>
    </VStack>
  </Box>
)

interface AppErrorBoundaryProps {
  children: React.ReactNode
  appName: AppName
}

const AppErrorBoundary: React.FC<AppErrorBoundaryProps> = ({ children, appName }) => {
  const [isReportingError, setIsReportingError] = useState(false)
  const [errorReported, setErrorReported] = useState<Date | null>(null)
  const [errorWhileReporting, setErrorWhileReporting] = useState<string | null>(null)
  const reportError = useCallback(
    async (errorStack: string) => {
      const error = {
        uid: auth.currentUser?.uid ?? null,
        errorStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        appName,
      }
      const errorsCollection = collection(db, SITE_ERRORS)
      try {
        setIsReportingError(true)
        await addDoc(errorsCollection, error)
        setErrorReported(new Date())
      } catch (error) {
        console.error('Error reporting error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        setErrorWhileReporting(errorMessage)
      } finally {
        setIsReportingError(false)
      }
    },
    [appName],
  )
  return (
    <ErrorBoundary
      FallbackComponent={props => (
        <ErrorFallback {...props}>
          <ErrorReportingStatus
            isReportingError={isReportingError}
            errorReported={errorReported}
            errorWhileReporting={errorWhileReporting}
          />
        </ErrorFallback>
      )}
      onReset={() => {
        // Reset the state of your app here
      }}
      onError={error => {
        // Log the error to an error reporting service
        reportError(error.stack ?? 'Unknown error')
        console.error(`Uncaught error: ${error.message}`)
      }}>
      {children}
    </ErrorBoundary>
  )
}

export default AppErrorBoundary
